import { SiteAvailabilityControl } from 'src/net-availability/enums/site-availability-control';
import {
  AssetNamesAndSiteLimitsError,
  getLimitsAndDeclaredAvailabilitiesGroupedByDateInterval,
  INetAvailabilityError,
  SiteLevelUndeclaredRatingPropertyError,
  SiteLevelUniformPerAssetDisaggregationError,
} from 'src/net-availability/net-availability';
import { getUniformPerAssetNetPowerAndEnergyRating } from 'src/net-availability/site-level-net-availability-per-asset-disaggregation';
import {
  DeclaredPowerAndEnergyRating,
  SiteDeclaredAvailability,
  SiteLevelNetAvailability,
  SiteLimitForNetAvailability,
} from 'src/net-availability/types';
import {
  getMinDeclaredPowerAndEnergyRating,
  getMinPowerAndEnergyRating,
} from 'src/net-availability/utils/power-and-energy-rating';
import { checkAssetNamesAndSiteLimits } from 'src/net-availability/utils/site-limit';
import { Result } from 'src/types/result';
import { traverseAllResults } from 'src/utils/result/utils';
import {
  DateInterval,
  getGroupedByContinuousDateIntervals,
} from 'src/utils/time-interval';

export const getSiteLimitAsMinDeclaredPowerAndEnergyRating = (
  siteLimit: SiteLimitForNetAvailability,
): DeclaredPowerAndEnergyRating => {
  const nameplateDeclaredPowerAndEnergyRating: DeclaredPowerAndEnergyRating = {
    maxImportPower: siteLimit.nameplateMaxImportPower,
    maxExportPower: siteLimit.nameplateMaxExportPower,
    energyCapacity: undefined,
  };

  const contractualDeclaredPowerAndEnergyRating: DeclaredPowerAndEnergyRating =
    {
      maxImportPower: siteLimit.contractualMaxImportPower,
      maxExportPower: siteLimit.contractualMaxExportPower,
      energyCapacity: siteLimit.contractualEnergyCapacity,
    };

  const gridConnectionDeclaredPowerAndEnergyRating: DeclaredPowerAndEnergyRating =
    {
      maxImportPower: siteLimit.gridConnectionMaxImportPower,
      maxExportPower: siteLimit.gridConnectionMaxExportPower,
      energyCapacity: undefined,
    };

  const siteLimitAsDeclaredPowerAndEnergyRatings: DeclaredPowerAndEnergyRating[] =
    [
      nameplateDeclaredPowerAndEnergyRating,
      contractualDeclaredPowerAndEnergyRating,
      gridConnectionDeclaredPowerAndEnergyRating,
    ];

  const minDeclaredPowerAndEnergyRating = getMinDeclaredPowerAndEnergyRating(
    siteLimitAsDeclaredPowerAndEnergyRatings,
  );

  return minDeclaredPowerAndEnergyRating;
};

export const getSiteLevelNetAvailabilities = ({
  siteLimits,
  siteDeclaredAvailabilities,
  assetNames,
  timeHorizon,
  additionalTimestamps,
}: {
  siteLimits: SiteLimitForNetAvailability[];
  siteDeclaredAvailabilities: SiteDeclaredAvailability[];
  assetNames: string[];
  timeHorizon: DateInterval;
  additionalTimestamps?: Parameters<
    typeof getGroupedByContinuousDateIntervals
  >[0]['additionalTimestamps'];
}): Result<INetAvailabilityError[], SiteLevelNetAvailability[]> => {
  const activeSiteDeclaredAvailabilities = siteDeclaredAvailabilities.filter(
    (item) => !item.isCancelled,
  );

  const limitsAndActiveDeclaredAvailabilitiesGroupedByDateInterval =
    getLimitsAndDeclaredAvailabilitiesGroupedByDateInterval({
      siteAvailabilityControl: SiteAvailabilityControl.SiteLevel,
      limits: siteLimits,
      declaredAvailabilities: activeSiteDeclaredAvailabilities,
      timeHorizon,
      additionalTimestamps,
    });

  const netAvailabilityResults: Result<
    INetAvailabilityError,
    SiteLevelNetAvailability
  >[] = limitsAndActiveDeclaredAvailabilitiesGroupedByDateInterval.map(
    ({
      start,
      end,
      limits: limitsForInterval,
      declaredAvailabilities: activeDeclaredAvailabilitiesForInterval,
    }) =>
      checkAssetNamesAndSiteLimits({
        assetNames,
        siteLimits: limitsForInterval,
      })
        .mapLeft(
          (cause) =>
            new AssetNamesAndSiteLimitsError(cause, {
              start,
              end,
              timeHorizon,
              siteLimitsForInterval: limitsForInterval,
              rawSiteLimits: siteLimits,
            }),
        )
        .flatMap((limitForInterval) => {
          const limitForIntervalAsMinDeclaredPowerAndEnergyRating =
            getSiteLimitAsMinDeclaredPowerAndEnergyRating(limitForInterval);

          return getMinPowerAndEnergyRating([
            limitForIntervalAsMinDeclaredPowerAndEnergyRating,
            ...activeDeclaredAvailabilitiesForInterval,
          ])
            .mapLeft(
              (cause) =>
                new SiteLevelUndeclaredRatingPropertyError(cause, {
                  start,
                  end,
                  timeHorizon,
                  siteLimitForInterval: limitForInterval,
                  rawSiteLimits: siteLimits,
                  limitForIntervalAsMinDeclaredPowerAndEnergyRating,
                  activeDeclaredAvailabilitiesForInterval,
                  rawSiteDeclaredAvailabilities: siteDeclaredAvailabilities,
                }),
            )
            .flatMap((netPowerAndEnergyRating) =>
              getUniformPerAssetNetPowerAndEnergyRating({
                assetNames,
                netPowerAndEnergyRating,
              })
                .mapLeft(
                  (cause) =>
                    new SiteLevelUniformPerAssetDisaggregationError(cause, {
                      start,
                      end,
                      timeHorizon,
                      assetNames,
                    }),
                )
                .map((perAsset) => {
                  const netAvailability: SiteLevelNetAvailability = {
                    siteAvailabilityControl: SiteAvailabilityControl.SiteLevel,
                    siteLimit: limitForInterval,
                    siteActiveDeclaredAvailabilities:
                      activeDeclaredAvailabilitiesForInterval,
                    start,
                    end,
                    netPowerAndEnergyRatings: {
                      site: netPowerAndEnergyRating,
                      perAsset,
                    },
                  };

                  return netAvailability;
                }),
            );
        }),
  );

  return traverseAllResults(netAvailabilityResults);
};
